import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { analyticsService } from '../services/analyticsService';
import { queryKeys } from '../api/queryClient';

function rangeKey(range) {
  return { from: range?.from || '', to: range?.to || '' };
}

export function useAnalyticsOverview(range) {
  const key = useMemo(() => rangeKey(range), [range?.from, range?.to]);
  const query = useQuery({
    queryKey: queryKeys.adminAnalyticsOverview(key),
    queryFn: () => analyticsService.overview(key),
    staleTime: 10_000,
  });
  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : 'Failed to load'
      : null,
  };
}

export function useAnalyticsArticles(range, sort = 'views') {
  const key = useMemo(() => rangeKey(range), [range?.from, range?.to]);
  const query = useQuery({
    queryKey: queryKeys.adminAnalyticsArticles(key, sort),
    queryFn: () => analyticsService.articles({ ...key, sort: 'viewsInRange' }),
    staleTime: 10_000,
  });
  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : 'Failed to load'
      : null,
  };
}

export function useAnalyticsArticle(id, range) {
  const key = useMemo(() => rangeKey(range), [range?.from, range?.to]);
  const query = useQuery({
    queryKey: queryKeys.adminAnalyticsArticle(id, key),
    queryFn: () => analyticsService.article(id, key),
    enabled: Boolean(id),
    staleTime: 10_000,
  });
  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : 'Failed to load'
      : null,
  };
}

export function useAnalyticsAlerts(range) {
  const key = useMemo(() => rangeKey(range), [range?.from, range?.to]);
  const query = useQuery({
    queryKey: queryKeys.adminAnalyticsAlerts(key),
    queryFn: () => analyticsService.alerts(key),
  });
  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : 'Failed to load'
      : null,
  };
}

export function useAnalyticsAudience(range) {
  const key = useMemo(() => rangeKey(range), [range?.from, range?.to]);
  const query = useQuery({
    queryKey: queryKeys.adminAnalyticsAudience(key),
    queryFn: () => analyticsService.audience(key),
  });
  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : 'Failed to load'
      : null,
  };
}

export function useAnalyticsOps(range) {
  const key = useMemo(() => rangeKey(range), [range?.from, range?.to]);
  const query = useQuery({
    queryKey: queryKeys.adminAnalyticsOps(key),
    queryFn: () => analyticsService.ops(key),
  });
  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : 'Failed to load'
      : null,
  };
}

export function defaultAnalyticsRange(days = 30) {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return {
    from: from.toISOString(),
    to: to.toISOString(),
    preset: days,
  };
}
